/*
  /family — create or manage your family. Owner sees member management +
  invite controls. Members see read-only roster + the option to leave.
*/
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Users, Plus, Mail, Loader2, Copy, Check, X, LogOut,
  Shield, Crown, Eye, UserCog, AlertCircle,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { family as familyApi } from "../lib/accountApi";
import { toast } from "sonner";

const ROLE_DEFS = {
  owner:  { label: "Owner",  icon: Crown,   desc: "Full control + manages members" },
  member: { label: "Member", icon: UserCog, desc: "Edits shared items" },
  viewer: { label: "Viewer", icon: Eye,     desc: "Read-only access" },
};

function RoleBadge({ role }) {
  const def = ROLE_DEFS[role] || ROLE_DEFS.viewer;
  const Icon = def.icon;
  return (
    <span data-testid={`role-badge-${role}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide"
          style={{ background: role === "owner" ? "var(--primary-soft)" : "var(--surface-2)",
                   color: role === "owner" ? "var(--primary-ink)" : "var(--text-muted)" }}>
      <Icon className="h-3 w-3" /> {def.label}
    </span>
  );
}

function CreateFamilyForm({ onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const f = await familyApi.create(name.trim());
      toast.success(`Family "${f.name}" created`);
      onCreated();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't create family");
    } finally { setBusy(false); }
  }
  return (
    <div className="card-tactile rounded-2xl p-6" data-testid="create-family-card">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center"
             style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}>
          <Users className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-display tracking-tight text-xl">Create your family</h2>
          <p className="text-sm mt-0.5" style={{ color: "var(--text-muted)" }}>
            Share prescriptions, shopping lists, and routines with the people you care for.
          </p>
        </div>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="label-caps block mb-1">Family name</span>
          <input
            data-testid="create-family-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={80}
            placeholder="e.g. The Lovelace family"
            className="w-full px-3 h-10 rounded-lg border bg-transparent text-sm"
            style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
          />
        </label>
        <button
          type="submit"
          disabled={busy || !name.trim()}
          data-testid="create-family-submit"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
          style={{ background: "var(--primary)" }}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create family
        </button>
      </form>
    </div>
  );
}

function InviteForm({ onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  async function send(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const inv = await familyApi.invite(email.trim(), role);
      toast.success(`Invitation sent to ${inv.email}`);
      setEmail("");
      onInvited();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't send invite");
    } finally { setBusy(false); }
  }
  return (
    <form onSubmit={send} className="flex flex-col sm:flex-row gap-2 items-start sm:items-stretch" data-testid="invite-form">
      <input
        type="email"
        required
        data-testid="invite-email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="someone@example.com"
        className="flex-1 px-3 h-10 rounded-lg border bg-transparent text-sm"
        style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
      />
      <select
        value={role}
        onChange={(e) => setRole(e.target.value)}
        data-testid="invite-role"
        className="px-3 h-10 rounded-lg border bg-transparent text-sm"
        style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
      >
        <option value="member">Member</option>
        <option value="viewer">Viewer</option>
      </select>
      <button
        type="submit"
        disabled={busy || !email.trim()}
        data-testid="invite-submit"
        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
        style={{ background: "var(--primary)" }}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
        Send invite
      </button>
    </form>
  );
}

function CopyLinkButton({ url, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch { toast.error("Couldn't copy"); }
      }}
      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium border"
      style={{ borderColor: "var(--border-subtle)", color: "var(--text-muted)" }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy link"}
    </button>
  );
}

export default function FamilyPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [fam, setFam] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && !user) nav("/login", { replace: true });
  }, [user, loading, nav]);

  async function reload() {
    setBusy(true);
    try {
      const f = await familyApi.me();
      setFam(f);
    } catch { setFam(null); }
    finally { setBusy(false); }
  }
  useEffect(() => { if (user) reload(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function leaveOrDissolve() {
    const isOwner = fam.role === "owner";
    const msg = isOwner
      ? "Dissolve the family? This removes all members and un-shares all family data."
      : "Leave this family? You'll lose access to shared items.";
    if (!window.confirm(msg)) return;
    try {
      await familyApi.leave();
      toast.success(isOwner ? "Family dissolved" : "Left the family");
      setFam(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't leave"); }
  }

  async function changeRole(memberId, role) {
    try {
      await familyApi.setRole(memberId, role);
      toast.success("Role updated");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update role"); }
  }

  async function removeMember(memberId, label) {
    if (!window.confirm(`Remove ${label} from the family?`)) return;
    try {
      await familyApi.removeMember(memberId);
      toast.success("Member removed");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
  }

  async function revokeInvite(id) {
    if (!window.confirm("Revoke this invite?")) return;
    try {
      await familyApi.revokeInvite(id);
      toast.success("Invite revoked");
      reload();
    } catch { toast.error("Couldn't revoke"); }
  }

  if (loading || (busy && !fam)) {
    return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  if (!fam) {
    return (
      <div className="max-w-2xl mx-auto py-2 space-y-6">
        <div>
          <h1 className="font-display tracking-tighter text-3xl sm:text-4xl mb-1" style={{ color: "var(--primary-ink)" }}>
            Family Mode
          </h1>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            <Link to="/" className="hover:underline">← Back to Hub</Link>
          </p>
        </div>
        <CreateFamilyForm onCreated={reload} />
      </div>
    );
  }

  const isOwner = fam.role === "owner";

  return (
    <div className="max-w-3xl mx-auto py-2 space-y-6" data-testid="family-page">
      <div>
        <h1 className="font-display tracking-tighter text-3xl sm:text-4xl mb-1" style={{ color: "var(--primary-ink)" }}>
          {fam.name}
        </h1>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          You're {isOwner ? "the owner" : "a " + fam.role}. <Link to="/" className="hover:underline">Back to Hub</Link>
        </p>
      </div>

      {/* Members */}
      <section className="card-tactile rounded-2xl p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display tracking-tight text-xl">Members ({fam.members.length})</h2>
        </div>
        <div className="space-y-2">
          {fam.members.map(m => (
            <div key={m.id} data-testid={`member-row-${m.id}`}
                 className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
                 style={{ borderColor: "var(--border-subtle)" }}>
              <div className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold text-white shrink-0"
                   style={{ background: "var(--primary)" }}>
                {(m.name || m.email).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {m.name || m.email} {m.user_id === user.id && <span style={{ color: "var(--text-muted)" }}>· you</span>}
                </div>
                <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{m.email}</div>
              </div>
              {isOwner && m.role !== "owner" ? (
                <>
                  <select
                    value={m.role}
                    onChange={(e) => changeRole(m.id, e.target.value)}
                    data-testid={`member-role-${m.id}`}
                    className="text-xs h-8 px-2 rounded border bg-transparent"
                    style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
                  >
                    <option value="member">Member</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button
                    onClick={() => removeMember(m.id, m.name || m.email)}
                    data-testid={`member-remove-${m.id}`}
                    className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[color:var(--surface-2)]"
                    style={{ color: "var(--text-muted)" }}
                    title="Remove from family"
                  ><X className="h-4 w-4" /></button>
                </>
              ) : (
                <RoleBadge role={m.role} />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Invites */}
      {isOwner && (
        <section className="card-tactile rounded-2xl p-5 sm:p-6">
          <h2 className="font-display tracking-tight text-xl mb-1">Invite people</h2>
          <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
            We'll email a magic link. They'll join automatically when they accept.
          </p>
          <InviteForm onInvited={reload} />
          {fam.invites.length > 0 && (
            <div className="mt-5">
              <div className="label-caps mb-2">Pending invites</div>
              <div className="space-y-2">
                {fam.invites.map(inv => (
                  <div key={inv.id} data-testid={`invite-row-${inv.id}`}
                       className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border px-3 py-2.5"
                       style={{ borderColor: "var(--border-subtle)" }}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{inv.email}</div>
                      <div className="text-xs flex items-center gap-2 mt-0.5" style={{ color: "var(--text-muted)" }}>
                        <RoleBadge role={inv.role} /> · sent {new Date(inv.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <CopyLinkButton url={inv.join_url} testid={`invite-copy-${inv.id}`} />
                      <button
                        onClick={() => revokeInvite(inv.id)}
                        data-testid={`invite-revoke-${inv.id}`}
                        className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[color:var(--surface-2)]"
                        style={{ color: "var(--text-muted)" }}
                      ><X className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Leave / Dissolve */}
      <section className="card-tactile rounded-2xl p-5 sm:p-6"
               style={{ borderColor: "color-mix(in srgb, var(--error) 35%, var(--border-subtle))" }}>
        <div className="flex items-start gap-3 mb-3">
          <AlertCircle className="h-5 w-5 shrink-0" style={{ color: "var(--error)" }} />
          <div>
            <h2 className="font-display tracking-tight text-lg" style={{ color: "var(--error)" }}>
              {isOwner ? "Dissolve family" : "Leave family"}
            </h2>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              {isOwner
                ? "Removes everyone, deletes pending invites, and un-shares all family-shared items."
                : "You'll lose access to anything currently shared with you."}
            </p>
          </div>
        </div>
        <button
          onClick={leaveOrDissolve}
          data-testid="family-leave"
          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold text-white"
          style={{ background: "var(--error)" }}
        >
          <LogOut className="h-4 w-4" />
          {isOwner ? "Dissolve family" : "Leave family"}
        </button>
      </section>
    </div>
  );
}
